<div
  class="container form-wrapper booking-details-form"
  [formGroup]="bookingDetailsForm">
  @if (showFreezeWarning) {
    <div class="row lmb-2">
      <rds-badge
        type="minimal"
        size="m"
        class="badge"
        label="Please note that short-term changes may impact the reliability of
        service delivery. For further assistance, please reach out to the event
        host or the service provider directly."
        color="yellow"></rds-badge>
    </div>
  }
  <div class="row">
    <div class="col-12 lmb-2">
      <p class="ui-heading-2-bold">Select booking parameters</p>
    </div>
  </div>
  <div class="row">
    <div class="col-12">
      <rds-form-field labelPosition="top">
        <rds-control-label> Event name </rds-control-label>
        <input rdsInput formControlName="eventName" />
        @if (bookingDetailsForm.controls.eventName.errors) {
          <rds-control-error>This field is required</rds-control-error>
        }
      </rds-form-field>
    </div>
  </div>
  <div class="row">
    <div class="col-12 pb-7">
      <div class="pb-5">
        <rds-switch [formControl]="bookOnBehalfControl">
          Book on behalf
        </rds-switch>
      </div>

      @if (bookOnBehalfControl.value) {
        <div class="on-behalf-list" formArrayName="onBehalfEmails">
          <!--
            Track the control, not the index: `formControlName` binds once per
            view, so an index-tracked view reused after a removal would stay
            wired to the deleted control.
          -->
          @for (
            onBehalfControl of onBehalfEmails.controls;
            track onBehalfControl;
            let index = $index
          ) {
            <div class="on-behalf-person">
              <div class="on-behalf-row">
                <bsh-user-search-autocomplete
                  class="on-behalf-input"
                  [config]="{
                    label:
                      index === 0
                        ? 'Booking requester'
                        : 'Additional booking requester',
                    placeholder: 'Search user by name or email',
                  }"
                  [excludeCurrentUser]="true"
                  [formControlName]="index">
                </bsh-user-search-autocomplete>
                <div class="on-behalf-remove">
                  <button
                    rds-icon-button
                    type="button"
                    size="m"
                    [disabled]="onBehalfControl.disabled"
                    [rdsTooltip]="'Remove this person'"
                    (click)="removeOnBehalfPerson(index)">
                    <rds-icon icon="delete" namespace="outlined"></rds-icon>
                  </button>
                </div>
              </div>
              @if (onBehalfControl.errors) {
                <div class="error mb-3">
                  <rds-icon
                    icon="warning_circle"
                    namespace="outlined"
                    class="error-icon c-300-dark-red"></rds-icon>
                  <p class="error-text c-300-dark-red">
                    @if (onBehalfControl.errors['onBehalfIsRequester']) {
                      The booking requester cannot be added as an on behalf
                      person
                    } @else {
                      This field is required
                    }
                  </p>
                </div>
              }
            </div>
          }
        </div>
        @if (onBehalfEmails.errors?.['duplicateOnBehalf']) {
          <div class="error mb-3">
            <rds-icon
              icon="warning_circle"
              namespace="outlined"
              class="error-icon c-300-dark-red"></rds-icon>
            <p class="error-text c-300-dark-red">
              The same person cannot be added twice
            </p>
          </div>
        }
        @if (canAddOnBehalfPerson) {
          <button rds-text-button type="button" (click)="addOnBehalfPerson()">
            <rds-icon icon="plus" namespace="outlined"></rds-icon>
            Add another person
          </button>
        }
      }
    </div>
  </div>
  <div class="row mb-3">
    <div class="col-4">
      <rds-form-field labelPosition="top">
        <rds-control-label>
          Event Start - End Date
          <rds-icon
            [rdsTooltip]="
              'Booking start time cannot be more than one year in advance'
            "
            icon="circle_info"
            namespace="outlined"></rds-icon>
        </rds-control-label>
        <rds-date-range-input
          [rangePicker]="rangePicker"
          [min]="minDate"
          [max]="yearInAdvance">
          <input
            rdsStartDate
            placeholder="Start date"
            formControlName="startDate" />
          <input rdsEndDate placeholder="End date" formControlName="endDate" />
        </rds-date-range-input>
        <rds-datepicker-toggle rds-control-suffix></rds-datepicker-toggle>
        <rds-date-range-picker
          #rangePicker
          showToggle="false"
          [dateClass]="dateClass">
        </rds-date-range-picker>
        @if (bookingDetailsForm.errors?.['dateTooFar']) {
          <rds-control-error>
            Booking start time cannot be more than one year in advance
          </rds-control-error>
        } @else if (bookingDetailsForm.controls.startDate.invalid) {
          <rds-control-error> Date cannot be from the past </rds-control-error>
        }
      </rds-form-field>
    </div>
    <div class="col-5">
      <div class="timepickers-container">
        <rds-form-field labelPosition="top">
          <rds-control-label
            >Start time
            @if (durationTooltipText) {
              <rds-icon
                [rdsTooltip]="durationTooltipText"
                icon="circle_info"
                namespace="outlined"></rds-icon>
            }
          </rds-control-label>
          <input
            rdsInput
            [matTimepicker]="startPicker"
            [placeholder]="'HH:mm'"
            matTimepickerMin="07:00"
            matTimepickerMax="23:00"
            formControlName="startHour" />
          <mat-timepicker-toggle rds-control-suffix [for]="startPicker" />
          <mat-timepicker #startPicker />
          @if (bookingDetailsForm.errors?.['timeNotValid']) {
            <rds-control-error>
              Start time should be before end time
            </rds-control-error>
          } @else if (bookingDetailsForm.errors?.['timeFromPast']) {
            <rds-control-error>
              Event cannot start in the past
            </rds-control-error>
          } @else if (bookingDetailsForm.errors?.['durationTooShort']) {
            <rds-control-error>
              Meeting duration is too short
            </rds-control-error>
          } @else if (bookingDetailsForm.errors?.['durationTooLong']) {
            <rds-control-error>
              Meeting duration is too long
            </rds-control-error>
          } @else if (bookingDetailsForm.errors?.['blockingOverlap']) {
            <rds-control-error>
              Meeting overlaps with blocking time <br />
              @for (
                blockingTime of blockingTimes;
                track blockingTime.uuid;
                let last = $last
              ) {
                {{ blockingTime.from }} - {{ blockingTime.to }}
                @if (!last) {
                  <br />
                }
              }
            </rds-control-error>
          }
        </rds-form-field>
        <rds-form-field labelPosition="top">
          <rds-control-label>
            End time
            @if (durationTooltipText) {
              <rds-icon
                [rdsTooltip]="durationTooltipText"
                icon="circle_info"
                namespace="outlined"></rds-icon>
            }
          </rds-control-label>
          <input
            rdsInput
            [matTimepicker]="endPicker"
            [placeholder]="'HH:mm'"
            matTimepickerMin="07:00"
            matTimepickerMax="23:00"
            formControlName="endHour" />
          <mat-timepicker-toggle rds-control-suffix [for]="endPicker" />
          <mat-timepicker #endPicker />
          @if (bookingDetailsForm.errors?.['timeNotValid']) {
            <rds-control-error>
              End time should be after start time
            </rds-control-error>
          }
        </rds-form-field>
      </div>
    </div>
    <div class="col-3">
      <rds-form-field labelPosition="top">
        <rds-control-label
          >No. of People
          <rds-icon
            [rdsTooltip]="capacityInfo()"
            icon="circle_info"
            namespace="outlined"></rds-icon
        ></rds-control-label>
        <input rdsInput type="number" formControlName="numberOfPeople" />
        @if (bookingDetailsForm.controls.numberOfPeople.errors?.['required']) {
          <rds-control-error>
            This field is required. {{ capacityInfo() }}
          </rds-control-error>
        }
        @if (bookingDetailsForm.controls.numberOfPeople.errors?.['max']) {
          <rds-control-error>
            Maximum capacity:
            {{
              bookingDetailsForm.controls.roomLayoutUUID.value?.capacity?.max ??
                maxRoomCapacity()
            }}
          </rds-control-error>
        }
        @if (bookingDetailsForm.controls.numberOfPeople.errors?.['min']) {
          <rds-control-error>
            Minimum capacity:
            {{
              bookingDetailsForm.controls.roomLayoutUUID.value?.capacity?.min ??
                minRoomCapacity()
            }}
          </rds-control-error>
        }
      </rds-form-field>
    </div>
  </div>
  @if (
    availableServices.length > 0 ||
    unavailableServices().length > 0 ||
    layouts().length > 0
  ) {
    <div class="row lmb-2">
      <div class="col-12">
        <p class="ui-heading-2-bold">Additional parameters</p>
      </div>
    </div>
  }
  <div class="row">
    @if (availableServices.length > 0 || unavailableServices().length > 0) {
      <div class="col-5">
        <div class="switch-wrapper">
          @if (availableServices.length > 0) {
            <p class="ui-label-m-bold c-700-grey-1">
              Request Additional Services
            </p>
            @for (service of availableServices; track service.id) {
              <div [formGroup]="roomServicesForm">
                <rds-switch
                  [labelPosition]="'after'"
                  formControlName="{{ service.type }}">
                  {{ service.name }}
                </rds-switch>
                @if (
                  roomServicesForm.get(service.type)?.value &&
                  service.description
                ) {
                  <p class="ui-label-m-bold">
                    {{ service.description }}
                  </p>
                }
              </div>
            }
            @if (freezeDays > 0) {
              <p class="ui-label-m-bold">
                Services are editable only {{ freezeDays }} days before an event
              </p>
            }
          }
          @if (unavailableServices().length > 0) {
            <p class="ui-label-m-bold c-700-grey-1">Unavailable Services</p>
            @for (service of unavailableServices(); track service.serviceType) {
              <div>
                <rds-switch [labelPosition]="'after'" disabled>
                  {{ service.serviceType | titlecase }} Service
                </rds-switch>
              </div>
            }
          }
        </div>
      </div>
    }

    @if (layouts().length > 0) {
      <div class="col-5 lmt-s-2">
        <rds-form-field labelPosition="top">
          <rds-control-label>
            Select Layout
            <rds-icon
              [rdsTooltip]="'Tooltip message'"
              icon="circle_info"
              namespace="outlined">
            </rds-icon>
          </rds-control-label>
          <rds-select formControlName="roomLayoutUUID">
            @if (!hasActiveLayouts()) {
              <rds-select-option [value]="null">None</rds-select-option>
            }
            @for (layout of layouts(); track layout.layoutDexUUID) {
              <rds-select-option
                [value]="layout"
                [disabled]="!layout.active && isEdit">
                {{ layout.layoutName }}
                @if (updatedLayoutIds().has(layout.layoutDexUUID)) {
                  (UPDATED)
                }
                {{
                  layout.layoutDexUUID === defaultLayoutId ? '(Default)' : ''
                }}
              </rds-select-option>
            }
          </rds-select>
          @if (
            bookingDetailsForm.controls.roomLayoutUUID.errors?.[
              'inactiveLayout'
            ]
          ) {
            <rds-control-error
              >Selected layout is no longer active. Please choose a different
              one.</rds-control-error
            >
          }
        </rds-form-field>
        <div class="layout-details">
          @if (bookingDetailsForm.controls.roomLayoutUUID.value) {
            <p class="ui-label-s">
              Layout capacity:
              {{
                bookingDetailsForm.controls.roomLayoutUUID.value.capacity.min
              }}
              -
              {{
                bookingDetailsForm.controls.roomLayoutUUID.value.capacity.max
              }}
            </p>
            <p class="ui-label-s">Layout setup time:</p>
            <p class="ui-label-s">
              {{
                bookingDetailsForm.controls.roomLayoutUUID.value.setupTime
                  .before
              }}
              minutes before -
              {{
                bookingDetailsForm.controls.roomLayoutUUID.value.setupTime.after
              }}
              minutes after
            </p>
          }
        </div>
      </div>
      <div class="col-2">
        @if (
          bookingDetailsForm.controls.roomLayoutUUID.value &&
          bookingDetailsForm.controls.roomLayoutUUID.value.imageUrl
        ) {
          <div class="layout-image">
            <img
              alt="layout-image"
              [ngSrc]="
                bookingDetailsForm.controls.roomLayoutUUID.value.imageUrl
              "
              fill />
          </div>
        }
      </div>
    }
  </div>
  @if (generalQuestionsEnabled) {
    <div class="row lmt-3 lmb-2">
      <div class="col-12">
        <p class="ui-heading-2-bold">General questions</p>
        <p class="ui-label-m p-1">
          Please let us know if a Healthcare Professional (HCP), a CEC or
          Externals will join. It might require Security & Compliance Support.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <rds-form-field labelPosition="top">
          <rds-control-label> CEC on site? </rds-control-label>
          <rds-select formControlName="cecOnSite" placeholder="Yes/No">
            <rds-select-option [value]="true">Yes</rds-select-option>
            <rds-select-option [value]="false">No</rds-select-option>
          </rds-select>
        </rds-form-field>
      </div>
      <div class="col-4">
        <rds-form-field labelPosition="top">
          <rds-control-label> HCP on site? </rds-control-label>
          <rds-select formControlName="hcpOnSite" placeholder="Yes/No">
            <rds-select-option [value]="true">Yes</rds-select-option>
            <rds-select-option [value]="false">No</rds-select-option>
          </rds-select>
        </rds-form-field>
      </div>
      <div class="col-4">
        <rds-form-field labelPosition="top">
          <rds-control-label> More than 20 Externals? </rds-control-label>
          <rds-select
            formControlName="moreThan20Externals"
            placeholder="Yes/No">
            <rds-select-option [value]="true">Yes</rds-select-option>
            <rds-select-option [value]="false">No</rds-select-option>
          </rds-select>
        </rds-form-field>
      </div>
    </div>
  }
  <div class="row">
    <div class="col-6">
      <p class="ui-heading-2-bold lmt-3 lmb-2">Budgeting</p>
      <rds-form-field labelPosition="top">
        <rds-control-label>WBS</rds-control-label>
        <input rdsInput formControlName="wbsCode" />
      </rds-form-field>
      <rds-form-field labelPosition="top">
        <rds-control-label>Cost Center</rds-control-label>
        <input rdsInput maxlength="10" formControlName="costCenter" />
        @if (bookingDetailsForm.controls.costCenter.invalid) {
          <rds-control-error
            >Cost center should have 10 alphanumeric
            characters</rds-control-error
          >
        }
      </rds-form-field>
    </div>
    <div class="col-6">
      <p class="ui-heading-2-bold lmt-3 lmb-2">Additional Information</p>
      <rds-form-field labelPosition="top">
        <rds-control-label>Provide additional information</rds-control-label>
        <textarea rdsInput formControlName="additionalInfo"></textarea>
      </rds-form-field>
    </div>
  </div>
  <div class="row">
    <div class="col-12 buttons-container">
      <button rds-secondary-button size="l" (click)="cancel()">Cancel</button>
      <button
        rds-primary-button
        size="l"
        [disabled]="bookingDetailsForm.invalid"
        (click)="confirm()">
        Confirm
      </button>
    </div>
  </div>
</div>
